import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useAsync } from "react-use";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { Loading } from "../../../widgets/Loading";
import { formatDistanceToNow } from "date-fns";

interface PendingApproval {
  id: string;
  tail_number: string;
  aircraft_id: string;
  fbo_id: string;
  tenant_id: string | null;
  created_at: string;
  notes: string | null;
  aircraft: {
    make: string;
    model: string;
  };
  fbo: {
    name: string;
  };
  users: {
    email: string;
  } | null;
}

const headerCellSx = {
  backgroundColor: "background.paper",
  fontWeight: 600,
  borderBottom: 2,
  borderColor: "divider",
};

export const PendingApprovalsTab: React.FC = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const snackbar = useSnackbar();
  const [processingId, setProcessingId] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const { loading, value: approvals } = useAsync(async () => {
    const { data, error } = await postgrest
      .from("pending_faa_approvals")
      .select(
        "id, tail_number, aircraft_id, fbo_id, tenant_id, created_at, notes, aircraft(make, model), fbo(name), users:created_by(email)"
      )
      .is("approved_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to fetch pending approvals:", error);
      return [];
    }

    return (data as PendingApproval[]) || [];
  }, [postgrest, refreshKey]);

  const handleApprove = async (approval: PendingApproval) => {
    if (!approval.aircraft?.make || !approval.aircraft?.model) {
      snackbar.notify({
        text: t("screens.pendingApprovalsTab.errors.missingData"),
        severity: "error",
      });
      return;
    }

    setProcessingId(approval.id);

    try {
      const manufacturer = approval.aircraft.make.toUpperCase();
      const model = approval.aircraft.model.toUpperCase();

      // Step 1: Check/insert faa_tail_numbers
      const { data: existingTail } = await postgrest
        .from("faa_tail_numbers")
        .select("manufacturer, model")
        .eq("tail_number", approval.tail_number)
        .maybeSingle();

      let faaManufacturer = manufacturer;
      let faaModel = model;

      if (!existingTail) {
        const { error } = await postgrest.from("faa_tail_numbers").insert({
          tail_number: approval.tail_number,
          manufacturer,
          model,
        });
        if (error) throw new Error(t("screens.pendingApprovalsTab.errors.createFaaTail", { message: error.message }));
      } else {
        faaManufacturer = existingTail.manufacturer;
        faaModel = existingTail.model;
      }

      // Step 2: Check/insert faa_to_airplx mapping
      const { data: existingMapping } = await postgrest
        .from("faa_to_airplx")
        .select("id")
        .eq("faa_manufacturer", faaManufacturer)
        .eq("faa_model", faaModel)
        .maybeSingle();

      if (!existingMapping) {
        const { error } = await postgrest.from("faa_to_airplx").insert({
          faa_manufacturer: faaManufacturer,
          faa_model: faaModel,
          matched_aircraft_id: approval.aircraft_id,
        });
        if (error) throw new Error(t("screens.pendingApprovalsTab.errors.createFaaMapping", { message: error.message }));
      }

      // Step 3: Mark as approved
      const { error: updateError } = await postgrest
        .from("pending_faa_approvals")
        .update({
          approved_at: new Date().toISOString(),
        })
        .eq("id", approval.id);

      if (updateError) throw new Error(t("screens.pendingApprovalsTab.errors.markApproved", { message: updateError.message }));

      snackbar.notify({
        text: t("screens.pendingApprovalsTab.success.approved", {
          tailNumber: approval.tail_number,
          make: approval.aircraft.make,
          model: approval.aircraft.model
        }),
        severity: "success",
      });

      setRefreshKey((k) => k + 1);
    } catch (error: unknown) {
      console.error("Failed to approve:", error);
      snackbar.notify({
        text: error instanceof Error ? error.message : t("screens.pendingApprovalsTab.errors.approveFailed"),
        severity: "error",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (approval: PendingApproval) => {
    setProcessingId(approval.id);

    try {
      // Delete the pending approval (and optionally the tenant that was created)
      const { error } = await postgrest
        .from("pending_faa_approvals")
        .delete()
        .eq("id", approval.id);

      if (error) throw error;

      snackbar.notify({
        text: t("screens.pendingApprovalsTab.success.rejected", { tailNumber: approval.tail_number }),
        severity: "success",
      });

      setRefreshKey((k) => k + 1);
    } catch (error: unknown) {
      console.error("Failed to reject:", error);
      snackbar.notify({
        text: t("screens.pendingApprovalsTab.errors.rejectFailed"),
        severity: "error",
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <Loading />
      </Box>
    );
  }

  if (!approvals || approvals.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <Typography variant="body1" color="text.secondary">
          {t("screens.pendingApprovalsTab.emptyState")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: "calc(100vh - 250px)" }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>{t("screens.pendingApprovalsTab.headers.tailNumber")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.pendingApprovalsTab.headers.aircraftType")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.pendingApprovalsTab.headers.fbo")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.pendingApprovalsTab.headers.submittedBy")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.pendingApprovalsTab.headers.submitted")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.pendingApprovalsTab.headers.tenant")}</TableCell>
            <TableCell align="center" width={120} sx={headerCellSx}>
              {t("screens.pendingApprovalsTab.headers.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {approvals.map((approval) => {
            const isProcessing = processingId === approval.id;
            return (
              <TableRow
                key={approval.id}
                sx={{ "&:hover": { backgroundColor: "action.hover" } }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {approval.tail_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {approval.aircraft?.make} {approval.aircraft?.model}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {approval.fbo?.name || t("screens.pendingApprovalsTab.unknown")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {approval.users?.email || t("screens.pendingApprovalsTab.unknown")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDistanceToNow(new Date(approval.created_at), {
                      addSuffix: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  {approval.tenant_id ? (
                    <Chip size="small" label={t("screens.pendingApprovalsTab.tenant.created")} variant="outlined" sx={{ borderColor: 'text.disabled', color: 'text.secondary' }} />
                  ) : (
                    <Chip size="small" label={t("screens.pendingApprovalsTab.tenant.noTenant")} variant="outlined" sx={{ borderColor: 'text.disabled', color: 'text.secondary' }} />
                  )}
                </TableCell>
                <TableCell align="center">
                  {isProcessing ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleApprove(approval)}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'success.main' } }}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleReject(approval)}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
